<template>
  <div class="container py-3">
    <h2 class="mb-3">Certificate Chain Explorer</h2>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-auto">
            <label class="form-label">Certificate ID</label>
            <input
              type="number"
              class="form-control"
              v-model="certificateId"
              placeholder="Enter certificate ID"
              :disabled="isLoading"
              @keyup.enter="loadChain"
            />
          </div>
          <div class="col-auto">
            <span class="text-body-secondary">or</span>
          </div>
          <div class="col-auto">
            <label class="form-label">Portfolio Item ID</label>
            <input
              type="number"
              class="form-control"
              v-model="portfolioItemId"
              placeholder="Enter portfolio item ID"
              :disabled="isLoading"
              @keyup.enter="loadChain"
            />
          </div>
          <div class="col-auto">
            <button
              class="btn btn-primary"
              @click="loadChain"
              :disabled="isLoading || (!certificateId && !portfolioItemId)"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              Load Chain
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="chainData" class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h5 class="mb-0">
          Chain from root certificate #{{ chainData.root_certificate_id }}
        </h5>
        <span class="badge bg-secondary">
          {{ chainData.certificates.length }} certificates,
          {{ chainData.portfolio_items.length }} portfolio items
        </span>
      </div>
      <div class="card-body">
        <CertificateChainTree
          :certificates="chainData.certificates"
          :portfolioItems="chainData.portfolio_items"
          :rootCertificateId="chainData.root_certificate_id"
        />
      </div>
    </div>

    <div v-else-if="!isLoading && hasSearched" class="alert alert-info">
      No chain data found. Try a different ID.
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import CertificateChainTree from "./CertificateChainTree.vue";
import type { ChainResponse } from "./certificateChainTypes";

const { globalErrorHandler } = useGlobalHandlers();
const { getQueryValue, parsers } = useQueryManager();

const certificateId = getQueryValue("certificate_id", parsers.number);
const portfolioItemId = getQueryValue("portfolio_item_id", parsers.number);

const isLoading = ref(false);
const hasSearched = ref(false);
const chainData = ref<ChainResponse | null>(null);

const loadChain = async () => {
  if (!certificateId.value && !portfolioItemId.value) return;

  isLoading.value = true;
  hasSearched.value = true;
  chainData.value = null;

  try {
    const params: Record<string, number> = {};
    if (certificateId.value) params.certificate_id = certificateId.value;
    else if (portfolioItemId.value)
      params.portfolio_item_id = portfolioItemId.value;

    const response = await goSolidApi.get<ChainResponse>(
      "/api/admin/portfolio/certificate-chain",
      { params }
    );
    chainData.value = response.data;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

if (certificateId.value || portfolioItemId.value) {
  loadChain();
}
</script>
